import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORE_KEY = "anyhomes_scroll_positions";

const SAVED = new Map();
try {
  const raw = sessionStorage.getItem(STORE_KEY);
  if (raw) JSON.parse(raw).forEach(([k, v]) => SAVED.set(k, v));
} catch (e) {
  /* ignore */
}

const persist = () => {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify([...SAVED]));
  } catch (e) {
    /* ignore */
  }
};

const getScroller = () => document.querySelector(".page-content-wrapper");

const readScroll = () => {
  const el = getScroller();
  if (el) return el.scrollTop;
  return window.scrollY || document.documentElement.scrollTop || 0;
};

const writeScroll = (y) => {
  const el = getScroller();
  if (el) {
    el.scrollTop = y;
  } else {
    window.scrollTo(0, y);
  }
};

/**
 * Restaure la position de scroll de la page précédente lors d'un retour
 * navigateur (navigation POP) et remet en haut pour PUSH/REPLACE.
 *
 * La position est capturée AVANT le changement de route (au clic sur un lien
 * ou sur popstate), car après le rendu de la nouvelle page le conteneur est
 * souvent déjà revenu à 0.
 */
const ScrollRestoration = () => {
  const location = useLocation();
  const navType = useNavigationType();
  const keyRef = useRef(location.key);
  keyRef.current = location.key;

  // Capture la position courante pour l'entrée d'historique courante.
  useEffect(() => {
    const save = () => {
      SAVED.set(keyRef.current, readScroll());
      persist();
    };
    const onClickCapture = () => save();
    const onPopState = () => save();

    window.addEventListener("pointerdown", onClickCapture, true);
    window.addEventListener("click", onClickCapture, true);
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("pointerdown", onClickCapture, true);
      window.removeEventListener("click", onClickCapture, true);
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  // Au changement de route : restaurer (POP) ou remonter (PUSH/REPLACE).
  useLayoutEffect(() => {
    const key = location.key;

    if (navType === "POP") {
      const target = SAVED.get(key) || 0;
      if (target > 0) {
        let attempts = 0;
        const tryRestore = () => {
          writeScroll(target);
          attempts += 1;
          if (attempts < 40 && Math.abs(readScroll() - target) > 2) {
            requestAnimationFrame(tryRestore);
          }
        };
        requestAnimationFrame(tryRestore);
      } else {
        writeScroll(0);
      }
    } else {
      writeScroll(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, navType]);

  return null;
};

export default ScrollRestoration;
